import { OrganizationRoles } from '../../../../components/UserInitialBox/OrganizationRoleStaticData';
import {
  Maybe,
  OrganizationInvitation,
  OrganizationUser,
  FullOrganizationInvitationFragment,
  Organization,
  OrganizationUserData,
} from '../../../../schema/generated/schema';

export type OrganizationActiveUser = {
  id: string;
  userData: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    role: string;
  };
  date: string;
  role: string;
  email: string | null;
};

export type OrganizationPendingUser = {
  id: string;
  userData: Maybe<OrganizationUserData>;
  date: string;
  role: string;
};

export const PAGINATION_PAGE_SIZE = 5;

const formatActiveMembers = (
  organizationUsers: OrganizationUser[]
): OrganizationActiveUser[] => {
  const sortedUsers = [...organizationUsers].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return sortedUsers.map((orgUser) => {
    const date = new Date(orgUser.date);
    return {
      id: orgUser.id,
      userData: {
        firstName: orgUser.federatedUser?.userData?.firstName || null,
        lastName: orgUser.federatedUser?.userData?.lastName || null,
        email: orgUser.federatedUser?.userData?.email || null,
        role: orgUser.role.name,
      },
      date: `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`,
      role: orgUser.role.name,
      email: orgUser.federatedUser?.userData?.email || null,
    };
  });
};

const formatPendingMembers = (
  invitations: OrganizationInvitation[]
): OrganizationPendingUser[] => {
  const invitationsCopy = [...invitations];

  invitationsCopy.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return invitationsCopy.map((invitation) => {
    const date = new Date(invitation.date);

    return {
      id: invitation.id,
      userData: invitation.userData,
      date: `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`,
      role: invitation.role?.name || 'Contributor',
    };
  });
};

/**
 * Counts the occurrences of each unique role in an array of members and returns an array of objects
 * with role names and their corresponding counts.
 */
const countMemberRoles = async (
  members: { role: string }[]
): Promise<{ role: string; count: number }[]> => {
  // Create a Map to store role counts.
  const reducedMap = new Map<string, number>();

  // Prepopulate with Admin and Contributor
  reducedMap.set(OrganizationRoles[1].toLocaleLowerCase(), 0);
  reducedMap.set(OrganizationRoles[2].toLocaleLowerCase(), 0);

  // Iterate through the members array.
  members.forEach((member) => {
    // Update the role count in the Map. If it doesn't exist, initialize it to 1.
    reducedMap.set(
      member.role.toLocaleLowerCase(),
      (reducedMap.get(member.role.toLocaleLowerCase()) || 0) + 1
    );
  });

  // Convert the Map into an array of objects with role and count properties and sort by Role.
  return Array.from(reducedMap)
    .map(([role, count]) => ({ role, count }))
    .sort((a, b) => {
      const roleA = a.role.toLowerCase();
      const roleB = b.role.toLowerCase();
      return roleA < roleB ? -1 : roleA > roleB ? 1 : 0;
    });
};
// check if the current user is the only admin and not the only user in the organization
const isCurrentUserOnlyAdmin = (
  organizationUsers: OrganizationActiveUser[],
  email: string
) => {
  let count = 0;
  const currentUser = organizationUsers.find(
    (orgUsr) => orgUsr.userData.email === email
  );
  if (currentUser?.role === 'Admin' && organizationUsers.length > 1) {
    organizationUsers.forEach((orgUser) => {
      if (orgUser.role === 'Admin') {
        count++;
      }
    });
  }
  return count == 1;
};

const getCurrentUserAdminRowId = (
  activeMembers: OrganizationActiveUser[],
  currentUserEmail: string
): number => {
  for (let i = 0; i < activeMembers.length; i++) {
    if (
      activeMembers[i].userData.email === currentUserEmail &&
      activeMembers[i].role == 'Admin'
    ) {
      return Number(activeMembers[i].id);
    }
  }
  return -1;
};

const fetchMembersInMemory = (
  members:
    | OrganizationActiveUser[]
    | OrganizationInvitation[]
    | FullOrganizationInvitationFragment[]
    | OrganizationUser[]
    | Organization[],
  setCurrentMembers: any,
  currentPage = 1,
  pageSize = 5
) => {
  const indexOfFirstRecord = (currentPage - 1) * pageSize;
  const indexOfLastRecord = indexOfFirstRecord + pageSize;

  const currentMembersRecord = members.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  setCurrentMembers(currentMembersRecord);
};

export default {
  formatActiveMembers,
  formatPendingMembers,
  countMemberRoles,
  isCurrentUserOnlyAdmin,
  getCurrentUserAdminRowId,
  fetchMembersInMemory,
};
